import React from 'react';
import { cn } from '../lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './Dialog';
import {
  Command as ShadcnCommand,
  CommandEmpty as ShadcnCommandEmpty,
  CommandGroup as ShadcnCommandGroup,
  CommandInput as ShadcnCommandInput,
  CommandItem as ShadcnCommandItem,
  CommandList as ShadcnCommandList,
  CommandSeparator as ShadcnCommandSeparator,
  CommandShortcut as ShadcnCommandShortcut,
} from './ui/command';

type CommandProps = React.ComponentPropsWithoutRef<typeof ShadcnCommand>;
type CommandDialogProps = React.ComponentPropsWithoutRef<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
  /** Light (default) or dark surface — forwarded to the underlying Dialog. */
  variant?: 'light' | 'dark';
};
type CommandEmptyProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandEmpty>;
type CommandGroupProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandGroup>;
type CommandInputProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandInput>;
type CommandItemProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandItem>;
type CommandListProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandList>;
type CommandSeparatorProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandSeparator>;
type CommandShortcutProps = React.ComponentPropsWithoutRef<typeof ShadcnCommandShortcut>;

export const Command: React.FC<CommandProps> = (props) => {
  return <ShadcnCommand {...props} />;
};

export const CommandDialog: React.FC<CommandDialogProps> = ({
  title = 'Command Palette',
  description = 'Search for a command to run...',
  children,
  className,
  showCloseButton = true,
  variant = 'light',
  ...props
}) => {
  // Composed from solink-ui's own Dialog primitives instead of the generated
  // ui/command.tsx CommandDialog (which wraps the raw ui/dialog.tsx), so this gets
  // Dialog's own `variant="dark"` — and every other Dialog fix — for free. Safe to
  // rewrite outright: this has no production consumers yet, only its own test.
  return (
    <Dialog {...props}>
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        variant={variant}
        showCloseButton={showCloseButton}
        className={cn('overflow-hidden p-0', className)}
      >
        <ShadcnCommand className="**:data-[slot=command-input-wrapper]:h-12 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </ShadcnCommand>
      </DialogContent>
    </Dialog>
  );
};

export const CommandEmpty: React.FC<CommandEmptyProps> = (props) => {
  return <ShadcnCommandEmpty {...props} />;
};

export const CommandGroup: React.FC<CommandGroupProps> = (props) => {
  return <ShadcnCommandGroup {...props} />;
};

export const CommandInput: React.FC<CommandInputProps> = (props) => {
  return <ShadcnCommandInput {...props} />;
};

export const CommandItem: React.FC<CommandItemProps> = (props) => {
  return <ShadcnCommandItem {...props} />;
};

export const CommandList: React.FC<CommandListProps> = (props) => {
  return <ShadcnCommandList {...props} />;
};

export const CommandSeparator: React.FC<CommandSeparatorProps> = (props) => {
  return <ShadcnCommandSeparator {...props} />;
};

export const CommandShortcut: React.FC<CommandShortcutProps> = (props) => {
  return <ShadcnCommandShortcut {...props} />;
};
